import { ConnectError } from '@connectrpc/connect';
import { ZodError } from 'zod';

import { leaveBreadcrumb } from 'cfa-react-logging';
import ooeConstants from '../constants';
import { d2cRestaurantClient, rexCateringClient } from '../shared/clients';
import { Location } from '../types/location';
import { restaurantSearchResponseToLocationResult } from '../user/transforms';
import { AddressResult, Order, OrderRequest, ValidatedDeliveryAddress } from './schemas';
import {
  addressResultToAutoComplete,
  locationToCateringDeliveryLocation,
  physicalToValidatedDeliveryAddress,
} from './transforms';

export const resubmitOrder = async ({
  order,
  cfaId,
  isRegisteredMember,
}: {
  order: OrderRequest;
  cfaId: string;
  isRegisteredMember: boolean;
}): Promise<Order> => {
  const bugsnag = {
    errorClass: ooeConstants.BUGSNAG_ERRORCLASS_ORDERS,
    context: 'Edit Existing Order',
    breadcrumb: 'Edit Existing Order',
  };

  leaveBreadcrumb(bugsnag.breadcrumb, bugsnag);

  return rexCateringClient.resubmitOrder({ ...order, cfaId, isRegisteredMember });
};

export const submitOrderApi = async ({
  order,
  cfaId,
  isRegisteredMember,
}: {
  order: OrderRequest;
  cfaId: string;
  isRegisteredMember: boolean;
}): Promise<Order> => {
  const bugsnag = {
    errorClass: ooeConstants.BUGSNAG_ERRORCLASS_ORDERS,
    context: 'Update Order',
    breadcrumb: 'Update Order',
  };

  leaveBreadcrumb(bugsnag.breadcrumb, bugsnag);

  return rexCateringClient.submitOrder({ ...order, cfaId, isRegisteredMember });
};

export const requestPaymentApi = async ({
  orderId,
  cfaId,
  isRegisteredMember,
}: {
  orderId: string;
  cfaId: string;
  isRegisteredMember: boolean;
}): Promise<void> => {
  const bugsnag = {
    errorClass: ooeConstants.BUGSNAG_ERRORCLASS_ORDERS,
    context: 'Request Payment Api',
    breadcrumb: 'Request Payment Api',
  };

  leaveBreadcrumb(bugsnag.breadcrumb, bugsnag);

  await rexCateringClient.requestPayment({ orderId, cfaId, isRegisteredMember });
};

export const cancelOrder = async ({
  orderId,
  cfaId,
  isRegisteredMember,
}: {
  orderId: string;
  cfaId: string;
  isRegisteredMember: boolean;
}): Promise<void> => {
  const bugsnag = {
    errorClass: ooeConstants.BUGSNAG_ERRORCLASS_ORDERS,
    context: 'Cancel Order',
    breadcrumb: 'Cancel Order',
  };

  leaveBreadcrumb(bugsnag.breadcrumb, bugsnag);

  await rexCateringClient.cancelOrder({ orderId, cfaId, isRegisteredMember });
};

export const initiateOrder = async ({
  order,
  cfaId,
  isRegisteredMember,
}: {
  order: OrderRequest;
  cfaId: string;
  isRegisteredMember: boolean;
}): Promise<Order> => {
  const bugsnag = {
    errorClass: ooeConstants.BUGSNAG_ERRORCLASS_ORDERS,
    context: 'Initiate Order',
    breadcrumb: 'Initiate Order',
  };

  leaveBreadcrumb(bugsnag.breadcrumb, bugsnag);

  return rexCateringClient.initiateOrder({ ...order, cfaId, isRegisteredMember });
};

export const validateAddressFromProtobuf = async (
  addressResult: AddressResult,
): Promise<ValidatedDeliveryAddress> => {
  const bugsnag = {
    errorClass: ooeConstants.BUGSNAG_ERRORCLASS_LOCATIONS,
    context: 'Validate Address (Protobuf)',
    breadcrumb: 'Validate Address (Protobuf)',
  };

  leaveBreadcrumb(bugsnag.breadcrumb, {
    message: 'Calling protobuf ValidateAddress RPC',
    placeId: addressResult.placeId,
    useProtobuf: true,
  });

  try {
    // Create the protobuf request using Zod schema
    const autoComplete = addressResultToAutoComplete.parse(addressResult);

    // Call the protobuf API
    const response = await d2cRestaurantClient.validateAddress({
      autocompleteResultWithSessionToken: {
        autocompleteResult: autoComplete,
      },
    });

    // Validate that we received a physical address in the response
    if (!response.physicalAddress) {
      throw new Error('No physical address returned from validation API');
    }

    // Convert the protobuf response to the legacy format using Zod schema
    const validatedAddress = physicalToValidatedDeliveryAddress.parse(response.physicalAddress);

    leaveBreadcrumb('Validate Address (Protobuf) - Success', {
      message: 'Successfully validated address via protobuf',
      hasStreetNumber: Boolean(validatedAddress.streetNumber),
      hasRoute: Boolean(validatedAddress.route),
    });

    return validatedAddress;
  } catch (error) {
    // Handle Zod validation errors
    if (error instanceof ZodError) {
      leaveBreadcrumb('Validate Address (Protobuf) - Error', {
        message: 'Zod validation error during address validation',
        zodIssues: error.issues,
        errorMessage: error.message,
      });
      throw new Error('Invalid address data format');
    }

    // Handle ConnectRPC-specific errors
    if (error instanceof ConnectError) {
      leaveBreadcrumb('Validate Address (Protobuf) - Error', {
        message: 'ConnectRPC error during address validation',
        code: error.code,
        errorMessage: error.message,
      });
      throw new Error(error.message || 'Address validation failed');
    }

    // Re-throw other errors
    leaveBreadcrumb('Validate Address (Protobuf) - Error', {
      message: 'Unexpected error during address validation',
      error: String(error),
    });
    throw error;
  }
};

export const getCateringDeliveryLocationsFromProtobuf = async ({
  placeId,
}: {
  placeId: string;
}): Promise<Location[]> => {
  leaveBreadcrumb('Get Catering Delivery Locations (Protobuf)', {
    message: 'Calling protobuf CateringDeliveryRestaurantSearch RPC',
    placeId,
  });

  try {
    const response = await d2cRestaurantClient.cateringDeliveryRestaurantSearch({ placeId });

    leaveBreadcrumb('Get Catering Delivery Locations (Protobuf) - Success', {
      message: 'CateringDeliveryRestaurantSearch succeeded',
      count: response.restaurants.length,
    });

    return response.restaurants
      .map((r) => restaurantSearchResponseToLocationResult.parse(r))
      .map(locationToCateringDeliveryLocation) as unknown as Location[];
  } catch (error) {
    if (error instanceof ConnectError) {
      leaveBreadcrumb('Get Catering Delivery Locations (Protobuf) - Error', {
        message: 'ConnectRPC error during catering delivery restaurant search',
        code: error.code,
        errorMessage: error.message,
      });
      throw new Error(error.message || 'Catering delivery restaurant search failed');
    }

    leaveBreadcrumb('Get Catering Delivery Locations (Protobuf) - Error', {
      message: 'Unexpected error during catering delivery restaurant search',
      error: String(error),
    });
    throw error;
  }
};
